// src/services/admin/adminService.ts
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { logInfo, logError, logDev } from '../../utils/logger';

// Firebase Functions 的類型定義
export interface AdminFeedbackListOptions {
  pageSize?: number;
  lastDocId?: string;
  searchUserId?: string;
  showUnreadOnly?: boolean;
  showUnresolvedOnly?: boolean;
}

export interface AdminFeedbackListResult {
  feedbacks: AdminFeedback[];
  hasMore: boolean;
  lastDocId: string | null;
}

export interface AdminFeedback {
  id: string;
  userId: string;
  userEmail: string;
  content: string;
  isRead: boolean;
  isResolved: boolean;
  createdAt: string; // ISO 字符串
  context: {
    currentPage: string;
    userAgent: string;
  };
  adminNotes?: string;
  resolvedAt?: string; // ISO 字符串
  readByAdmin?: string;
  resolvedByAdmin?: string;
}

/**
 * 檢查當前用戶的管理員狀態
 */
export const checkAdminStatus = async (): Promise<{
  isAdmin: boolean;
  userId?: string;
  claims?: Record<string, any>;
}> => {
  try {
    logDev('AdminService', 'Creating checkAdminStatus function');
    const checkAdminStatusFn = httpsCallable(functions, 'checkAdminStatus');

    logDev('AdminService', 'Calling checkAdminStatus Cloud Function');
    const result = await checkAdminStatusFn({});

    logDev('AdminService', 'Cloud Function response received', result);

    return result.data as any;
  } catch (error) {
    logError('AdminService', 'Cloud Function call failed', error);
    // 修復：拋出錯誤而非靜默返回 false，讓上層能捕獲並顯示給用戶
    throw error;
  }
};

// initializeFirstAdmin 函數已移除
// 開發環境設置管理員請參考 functions/src/admin/userManagement.js 中的說明

// setAdminClaim 函數已移除
// 管理員權限變更請參考 functions/src/admin/userManagement.js 中的說明

/**
 * 獲取管理員回饋列表（受保護）
 */
export const getAdminFeedbackList = async (options: AdminFeedbackListOptions = {}): Promise<AdminFeedbackListResult> => {
  try {
    const getAdminFeedbackListFn = httpsCallable(functions, 'getAdminFeedbackList');
    const result = await getAdminFeedbackListFn(options);
    return result.data as any;
  } catch (error) {
    logError('AdminService', 'Get admin feedback list failed', error);
    throw error;
  }
};

/**
 * 標記回饋為已讀（受保護）
 */
export const markFeedbackAsRead = async (feedbackId: string): Promise<void> => {
  try {
    logDev('AdminService', 'Creating markFeedbackAsRead Cloud Function handle');
    const markFeedbackAsReadFn = httpsCallable(functions, 'markFeedbackAsRead');

    logDev('AdminService', 'Calling markFeedbackAsRead Cloud Function', { feedbackId });
    const result = await markFeedbackAsReadFn({ feedbackId });

    logDev('AdminService', 'markFeedbackAsRead response received', result);
  } catch (error) {
    logError('AdminService', 'markFeedbackAsRead Cloud Function call failed', error);
    throw error;
  }
};

/**
 * 標記回饋為已解決（受保護）
 */
export const markFeedbackAsResolved = async (feedbackId: string, adminNotes?: string): Promise<void> => {
  try {
    logDev('AdminService', 'Creating markFeedbackAsResolved Cloud Function handle');
    const markFeedbackAsResolvedFn = httpsCallable(functions, 'markFeedbackAsResolved');

    logDev('AdminService', 'Calling markFeedbackAsResolved Cloud Function', { feedbackId, adminNotes });
    const result = await markFeedbackAsResolvedFn({ feedbackId, adminNotes });

    logDev('AdminService', 'markFeedbackAsResolved response received', result);
  } catch (error) {
    logError('AdminService', 'markFeedbackAsResolved Cloud Function call failed', error);
    throw error;
  }
};

/**
 * 批量標記為已讀（受保護）
 */
export const batchMarkFeedbackAsRead = async (feedbackIds: string[]): Promise<{
  success: boolean;
  updatedCount: number;
}> => {
  try {
    const batchMarkFeedbackAsReadFn = httpsCallable(functions, 'batchMarkFeedbackAsRead');
    const result = await batchMarkFeedbackAsReadFn({ feedbackIds });
    return result.data as any;
  } catch (error) {
    logError('AdminService', 'Batch mark as read failed', error);
    throw error;
  }
};

/**
 * 獲取未讀回饋數量（受保護）
 */
export const getUnreadFeedbackCount = async (): Promise<number> => {
  try {
    const getUnreadFeedbackCountFn = httpsCallable(functions, 'getUnreadFeedbackCount');
    const result = await getUnreadFeedbackCountFn({});
    return (result.data as any).count;
  } catch (error) {
    logError('AdminService', 'Get unread feedback count failed', error);
    return 0;
  }
};

// ========================================
// 用戶訂閱管理 API
// ========================================

/**
 * 用戶訂閱資訊
 */
export interface AdminUserSubscription {
  plan: 'free' | 'pro';
  status: 'none' | 'active' | 'canceled' | 'trialing';
  billingCycle?: 'monthly' | 'yearly' | 'lifetime';
  expiredDate?: string;
  isInternal?: boolean;
  grantedBy?: string;
  grantReason?: string;
}

/**
 * 用戶資料
 */
export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
  subscription: AdminUserSubscription;
}

/**
 * 用戶列表查詢選項
 */
export interface AdminUserListOptions {
  pageSize?: number;
  lastDocId?: string;
  emailPrefix?: string;
  filterPlan?: 'free' | 'pro' | null;
  filterInternal?: boolean | null;
}

/**
 * 用戶列表查詢結果
 */
export interface AdminUserListResult {
  success: boolean;
  users: AdminUser[];
  hasMore: boolean;
  lastDocId: string | null;
}

/**
 * 用戶詳細資訊
 */
export interface AdminUserDetail {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
  subscription: {
    plan: 'free' | 'pro';
    status: string;
    billingCycle?: string;
    expiredDate?: string;
    originalStartDate?: string;
    completedCycles?: number;
    isInternal?: boolean;
    grantedBy?: string;
    grantReason?: string;
    polarCustomerId?: string;
    polarSubscriptionId?: string;
    lastProcessedAt?: string;
    updatedAt?: string;
  };
  events: Array<{
    id: string;
    eventType: string;
    adminUserId?: string;
    adminEmail?: string;
    reason?: string;
    timestamp: string;
    metadata?: any;
  }>;
}

/**
 * 獲取用戶列表（管理員）
 */
export const getAdminUserList = async (options: AdminUserListOptions = {}): Promise<AdminUserListResult> => {
  try {
    const getAdminUserListFn = httpsCallable(functions, 'getAdminUserList');
    const result = await getAdminUserListFn(options);
    return result.data as any;
  } catch (error) {
    logError('AdminService', 'Get admin user list failed', error);
    throw error;
  }
};

/**
 * 授予用戶永久 Pro 權限
 */
export const grantLifetimePro = async (userId: string, reason: string): Promise<{
  success: boolean;
  message: string;
  details: any;
}> => {
  try {
    logInfo('AdminService', 'Calling grantLifetimePro Cloud Function', { userId, reason });
    const grantLifetimeProFn = httpsCallable(functions, 'grantLifetimePro');
    const result = await grantLifetimeProFn({ userId, reason });
    logInfo('AdminService', 'grantLifetimePro succeeded', result.data);
    return result.data as any;
  } catch (error) {
    logError('AdminService', 'grantLifetimePro failed', error);
    throw error;
  }
};

/**
 * 撤銷用戶的永久 Pro 權限
 */
export const revokeLifetimePro = async (userId: string, reason: string): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    logInfo('AdminService', 'Calling revokeLifetimePro Cloud Function', { userId, reason });
    const revokeLifetimeProFn = httpsCallable(functions, 'revokeLifetimePro');
    const result = await revokeLifetimeProFn({ userId, reason });
    logInfo('AdminService', 'revokeLifetimePro succeeded', result.data);
    return result.data as any;
  } catch (error) {
    logError('AdminService', 'revokeLifetimePro failed', error);
    throw error;
  }
};

// ========================================
// Dashboard 統計 API
// ========================================

export interface AdminDashboardSubscriptionEvent {
  id: string;
  eventType: string | null;
  userEmail: string | null;
  userId: string | null;
  adminEmail: string | null;
  reason: string | null;
  timestamp: string | null;
}

export interface AdminDashboardFeedbackItem {
  id: string;
  userId: string | null;
  userEmail: string | null;
  content: string;
  createdAt: string | null;
}

export interface AdminDashboardStats {
  stats: {
    totalUsers: number;
    proUsers: number;
    trialUsers: number;
    unresolvedFeedback: number;
  };
  recentSubscriptionEvents: AdminDashboardSubscriptionEvent[];
  recentUnreadFeedback: AdminDashboardFeedbackItem[];
}

/**
 * 獲取 Admin Dashboard 統計資料
 */
export const getAdminDashboardStats = async (): Promise<AdminDashboardStats> => {
  try {
    const fn = httpsCallable(functions, 'getAdminDashboardStats');
    const result = await fn({});
    return result.data as AdminDashboardStats;
  } catch (error) {
    logError('AdminService', 'Get admin dashboard stats failed', error);
    throw error;
  }
};

/**
 * 獲取用戶詳細訂閱資訊
 */
export const getUserSubscriptionDetail = async (userId: string): Promise<{
  success: boolean;
  user: AdminUserDetail;
}> => {
  try {
    const getUserSubscriptionDetailFn = httpsCallable(functions, 'getUserSubscriptionDetail');
    const result = await getUserSubscriptionDetailFn({ userId });
    return result.data as any;
  } catch (error) {
    logError('AdminService', 'Get user subscription detail failed', error);
    throw error;
  }
};